type FetchInput = Parameters<typeof fetch>[0]
type FetchInit = Parameters<typeof fetch>[1]

export interface FetchWithTimeoutOptions {
  timeoutMs: number
  onTimeoutStart?: () => void
  onTimeoutError?: () => void
  createTimeoutError?: () => Error
}

export const fetchWithTimeout = async (
  input: FetchInput,
  init: FetchInit = {},
  { timeoutMs, onTimeoutStart, onTimeoutError, createTimeoutError }: FetchWithTimeoutOptions
): Promise<Response> => {
  const controller = new AbortController()
  const upstreamSignal = init?.signal
  let timedOut = false

  const upstreamAbortHandler = () => {
    controller.abort()
  }

  if (upstreamSignal) {
    if (upstreamSignal.aborted) {
      controller.abort()
    } else {
      upstreamSignal.addEventListener('abort', upstreamAbortHandler, { once: true })
    }
  }

  const timeoutId = setTimeout(() => {
    timedOut = true
    onTimeoutStart?.()
    controller.abort()
  }, timeoutMs)

  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    })
  } catch (error) {
    if (timedOut) {
      onTimeoutError?.()
      if (createTimeoutError) {
        throw createTimeoutError()
      }
    }

    throw error
  } finally {
    clearTimeout(timeoutId)
    if (upstreamSignal) {
      upstreamSignal.removeEventListener('abort', upstreamAbortHandler)
    }
  }
}
